import { apiClient } from './api';
import {
  SceneStartRequest,
  SceneStartResponse,
  SceneIterateRequest,
  SceneIterateResponse,
  SceneSession,
  SceneHistory,
} from '../models/scene';

const SESSION_STORAGE_KEY = 'sceneSessions';

export class SceneService {
  // API calls
  static async startSession(request: SceneStartRequest): Promise<SceneStartResponse> {
    const response = await apiClient.post('/api/scene/session/start', request);
    return response.data;
  }

  static async iterate(sessionId: string, request: SceneIterateRequest): Promise<SceneIterateResponse> {
    const response = await apiClient.post(`/api/scene/session/${sessionId}/iterate`, request);
    return response.data;
  }

  // Local session management
  static createLocalSession(apiSessionId: string, concept: string, scene: any, sessionName?: string): SceneSession {
    const session: SceneSession = {
      id: `scene-${Date.now()}`,
      sessionId: apiSessionId,
      initialConcept: concept,
      currentScene: scene,
      history: [{
        scene,
        timestamp: new Date().toISOString(),
      }],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.saveSession(session);
    return session;
  }

  static getSession(id: string): SceneSession | null {
    const sessions = this.getAllSessions();
    return sessions.find(s => s.id === id) || null;
  }

  static getAllSessions(): SceneSession[] {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  static saveSession(session: SceneSession): void {
    const sessions = this.getAllSessions();
    const index = sessions.findIndex(s => s.id === session.id);
    
    if (index >= 0) {
      sessions[index] = session;
    } else {
      sessions.push(session);
    }

    session.updatedAt = new Date().toISOString();
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions));
  }

  static deleteSession(id: string): void {
    const sessions = this.getAllSessions();
    const filtered = sessions.filter(s => s.id !== id);
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(filtered));
  }

  static addHistory(sessionId: string, scene: any, feedback?: string, changes?: string[]): void {
    const session = this.getSession(sessionId);
    if (session) {
      session.currentScene = scene;
      session.history.push({
        scene,
        feedback,
        changes,
        timestamp: new Date().toISOString(),
      });
      this.saveSession(session);
    }
  }

  static exportScene(session: SceneSession): string {
    const scene = session.currentScene;
    let content = `# Scene: ${scene.title}\n\n`;
    
    // Setting
    content += `## Setting\n\n`;
    content += `**Location:** ${scene.setting.location}\n`;
    if (scene.setting.period) content += `**Period:** ${scene.setting.period}\n`;
    content += `**Environment:** ${scene.setting.environment}\n`;
    if (scene.setting.layout) content += `\n**Layout:**\n${scene.setting.layout}\n`;
    if (scene.setting.importantObjects?.length) {
      content += `\n**Important Objects:**\n`;
      scene.setting.importantObjects.forEach(obj => content += `- ${obj}\n`);
    }
    content += '\n';

    // Atmosphere
    content += `## Atmosphere\n\n`;
    content += `**Mood:** ${scene.atmosphere.mood}\n`;
    content += `**Overall Feeling:** ${scene.atmosphere.overallFeeling}\n`;
    if (scene.atmosphere.tension) content += `**Tension:** ${scene.atmosphere.tension}\n`;
    if (scene.atmosphere.lightingDescription) {
      content += `**Lighting:** ${scene.atmosphere.lightingDescription}\n`;
    }
    content += '\n';

    // Time and Weather
    if (scene.timeAndWeather) {
      content += `## Time and Weather\n\n`;
      content += `**Time of Day:** ${scene.timeAndWeather.timeOfDay}\n`;
      if (scene.timeAndWeather.weather) content += `**Weather:** ${scene.timeAndWeather.weather}\n`;
      if (scene.timeAndWeather.season) content += `**Season:** ${scene.timeAndWeather.season}\n`;
      if (scene.timeAndWeather.lightingConditions) {
        content += `**Lighting Conditions:** ${scene.timeAndWeather.lightingConditions}\n`;
      }
      content += '\n';
    }

    // Characters
    if (scene.characters?.length) {
      content += `## Characters Present\n\n`;
      scene.characters.forEach(char => {
        content += `### ${char.name} (${char.role})\n`;
        if (char.position) content += `**Position:** ${char.position}\n`;
        if (char.action) content += `**Action:** ${char.action}\n`;
        if (char.emotion) content += `**Emotion:** ${char.emotion}\n`;
        content += '\n';
      });
    }

    // Action
    content += `## Action\n\n`;
    content += `**Main Action:** ${scene.action.mainAction}\n`;
    if (scene.action.conflictPresent) {
      content += `\n**Conflict:** ${scene.action.conflictPresent}\n`;
    }
    if (scene.action.movementAndBlocking) {
      content += `\n**Movement and Blocking:**\n${scene.action.movementAndBlocking}\n`;
    }
    if (scene.action.backgroundActivity?.length) {
      content += `\n**Background Activity:**\n`;
      scene.action.backgroundActivity.forEach(activity => content += `- ${activity}\n`);
    }
    content += '\n';

    // Sensory Details
    content += `## Sensory Details\n\n`;
    content += `**Visual:** ${scene.sensoryDetails.visual}\n`;
    if (scene.sensoryDetails.auditory) content += `**Auditory:** ${scene.sensoryDetails.auditory}\n`;
    if (scene.sensoryDetails.olfactory) content += `**Olfactory:** ${scene.sensoryDetails.olfactory}\n`;
    if (scene.sensoryDetails.tactile) content += `**Tactile:** ${scene.sensoryDetails.tactile}\n`;
    if (scene.sensoryDetails.taste) content += `**Taste:** ${scene.sensoryDetails.taste}\n`;
    content += '\n';

    // Dialogue
    if (scene.dialogue?.length) {
      content += `## Dialogue\n\n`;
      scene.dialogue.forEach(d => {
        content += `**${d.speaker}:** "${d.text}"\n`;
        if (d.subtext) content += `*Subtext: ${d.subtext}*\n`;
        if (d.deliveryNotes) content += `*Delivery: ${d.deliveryNotes}*\n`;
        content += '\n';
      });
    }

    // Additional Details
    if (scene.emotionalTone) {
      content += `## Emotional Tone\n\n${scene.emotionalTone}\n\n`;
    }
    
    if (scene.pacing) {
      content += `## Pacing\n\n${scene.pacing}\n\n`;
    }
    
    if (scene.keyElements?.length) {
      content += `## Key Elements\n\n`;
      scene.keyElements.forEach(element => content += `- ${element}\n`);
      content += '\n';
    }

    // Development History
    content += `## Development History\n\n`;
    session.history.forEach((h, i) => {
      if (i === 0) {
        content += `### Initial Concept (${new Date(h.timestamp).toLocaleString()})\n\n`;
        content += `"${session.initialConcept}"\n\n`;
      } else {
        content += `### Iteration ${i} (${new Date(h.timestamp).toLocaleString()})\n\n`;
        if (h.feedback) content += `**Feedback:** ${h.feedback}\n\n`;
        if (h.changes?.length) {
          content += `**Changes:**\n`;
          h.changes.forEach(c => content += `- ${c}\n`);
          content += '\n';
        }
      }
    });

    return content;
  }
}